import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { dramas, genreOptions, topics } from "@/mocks/dramas";
import { defaultFilters, filterDramas, type FilterState } from "@/utils/drama";

const PAGE_SIZE = 12;

const sortOptions = [
  { value: "latest", label: "最近更新" },
  { value: "hot", label: "受欢迎程度" },
  { value: "rating", label: "评分最高" },
];

function parseFilters(params: URLSearchParams): FilterState {
  return {
    categories: params.getAll("category").filter(Boolean),
    genres: params.getAll("genre").filter(Boolean),
    year: params.get("year") ?? "all",
    sort: params.get("sort") ?? "latest",
  };
}

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => parseFilters(searchParams));
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const urlKey = searchParams.toString();
  useEffect(() => {
    setFilters(parseFilters(new URLSearchParams(urlKey)));
    setPage(1);
    setCurrentPage(1);
  }, [urlKey]);

  useEffect(() => {
    document.title = "国产影视分类 - 国产精品视频久久";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "description",
        "按类型、年份筛选国产影视资源，电影与电视剧分类浏览，支持最近更新、受欢迎程度与评分排序。",
      );
    }
  }, []);

  const topicId = searchParams.get("topic");
  const activeTopic = topics.find((t) => t.id === topicId);

  const baseList = useMemo(() => {
    if (activeTopic) return dramas.filter((item) => item.topics.includes(activeTopic.id));
    return dramas;
  }, [activeTopic]);

  const result = useMemo(() => filterDramas(baseList, filters), [baseList, filters]);
  const visible = result.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < result.length;

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
    const params = new URLSearchParams();
    next.categories.forEach((c) => params.append("category", c));
    next.genres.forEach((g) => params.append("genre", g));
    if (next.year !== "all") params.set("year", next.year);
    if (next.sort !== "latest") params.set("sort", next.sort);
    if (activeTopic) params.set("topic", activeTopic.id);
    setSearchParams(params, { replace: true });
  };

  const reset = () => {
    const next = { ...defaultFilters };
    setFilters(next);
    setPage(1);
    const params = new URLSearchParams();
    if (activeTopic) params.set("topic", activeTopic.id);
    setSearchParams(params, { replace: true });
  };

  const breadcrumbLabel =
    activeTopic?.name ??
    (filters.categories.length === 1
      ? filters.categories[0] === "movie"
        ? "电影"
        : "电视剧"
      : "全部作品");

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1 pt-16">
        <div className="w-full bg-background-100 px-4 pb-8 pt-8 md:px-8 md:pb-10 md:pt-10">
          <div className="mx-auto w-full max-w-6xl">
            <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <Link to="/category" className="transition-colors hover:text-primary-600 cursor-pointer">
                影视分类
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-900">{breadcrumbLabel}</span>
            </nav>

            <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  {activeTopic ? activeTopic.name : "国产影视分类"}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-foreground-700">
                  {activeTopic
                    ? `${activeTopic.desc}。可按类型、年份继续筛选，找到更贴合口味的作品。`
                    : "按类型、年份筛选国产影视资源，电影与电视剧分开浏览，也可以组合条件精确查找。"}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-database-2-line" />
                </span>
                共收录 <strong className="text-foreground-900">{result.length}</strong> 部作品
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600">专题：</span>
              {topics.map((topic) => (
                <Link
                  key={topic.id}
                  to={`/category?topic=${topic.id}`}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    topicId === topic.id
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400"
                  }`}
                >
                  {topic.name}
                </Link>
              ))}
              {topicId ? (
                <Link
                  to="/category"
                  className="text-xs text-foreground-500 underline-offset-2 hover:text-primary-600 hover:underline cursor-pointer"
                >
                  清除专题
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 lg:flex-row lg:gap-8">
            <aside className="hidden w-60 shrink-0 lg:block">
              <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-4">
                <FilterPanel
                  filters={filters}
                  genres={genreOptions}
                  onChange={updateFilters}
                  onReset={reset}
                  resultCount={result.length}
                />
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1.5 text-xs text-foreground-800 cursor-pointer lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                  </button>
                  <span className="text-xs text-foreground-600">
                    排序
                  </span>
                  <div className="flex rounded-full border border-background-300 bg-background-100 px-1 py-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateFilters({ ...filters, sort: option.value })}
                        className={`rounded-full px-2.5 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                          filters.sort === option.value
                            ? "bg-primary-600 text-background-50"
                            : "text-foreground-700 hover:text-primary-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="flex items-center gap-1 text-xs text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>

              {result.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    没有找到符合条件的影视作品
                  </h3>
                  <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-600">
                    试试减少筛选条件，或把年份范围放宽一些，也可以直接浏览全部作品。
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选条件
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
                    {visible.map((item) => (
                      <DramaCard
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        poster={item.poster}
                        year={item.year}
                        category={item.category}
                        genres={item.genres}
                        rating={item.rating}
                        tags={item.tags}
                        badge={item.category === "tv" ? "剧集" : undefined}
                      />
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col items-center gap-3">
                    <p className="text-xs text-foreground-600">
                      已显示 {visible.length} / {result.length} 部作品
                    </p>
                    {hasMore ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPage((p) => p + 1);
                          setCurrentPage((p) => p + 1);
                        }}
                        className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        加载更多
                        <i className="ri-arrow-down-line" />
                      </button>
                    ) : (
                      <span className="text-xs text-foreground-500">已经到底啦</span>
                    )}
                    <div className="hidden items-center gap-1 md:flex">
                      {Array.from({ length: Math.max(1, Math.ceil(result.length / PAGE_SIZE)) }).map(
                        (_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setPage(index + 1);
                              setCurrentPage(index + 1);
                            }}
                            className={`h-8 min-w-8 rounded-md px-2 text-xs transition-colors cursor-pointer ${
                              currentPage === index + 1
                                ? "bg-primary-600 text-background-50"
                                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                            }`}
                          >
                            {index + 1}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />

      {drawerOpen ? (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-lg bg-background-50 p-4 animate-fade-up">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选影视</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              genres={genreOptions}
              onChange={updateFilters}
              onReset={reset}
              resultCount={result.length}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看 {result.length} 部作品
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}